import React from 'react';

export type QuoteTermsShape = {
  payment_terms?: string;
  deposit?: {
    type?: 'none' | 'percent' | 'fixed';
    value?: number;
    due?: 'on_acceptance' | 'before_trip' | 'with_invoice';
    due_hours_before_service?: number | null;
  };
  cancellation_policy?: {
    rules?: Array<{
      hours_before_pickup: number;
      refund_percent: number;
      description?: string;
    }>;
  };
  quote_expiration_hours?: number;
  invoice_delivery?: 'on_acceptance' | 'after_trip' | 'manual';
  payment_required_to_confirm?: boolean;
  minimum_hours?: number;
  service_charge_percent?: number;
  gratuity_percent?: number;
  tax_rate?: number;
  terms_text?: string;
};

const DEFAULT_TERMS: QuoteTermsShape = {
  payment_terms: 'due_on_receipt',
  deposit: {
    type: 'percent',
    value: 20,
    due: 'on_acceptance',
    due_hours_before_service: null,
  },
  cancellation_policy: {
    rules: [
      {
        hours_before_pickup: 48,
        refund_percent: 100,
        description: 'Full refund',
      },
      {
        hours_before_pickup: 24,
        refund_percent: 50,
        description: '50% refund',
      },
      { hours_before_pickup: 0, refund_percent: 0, description: 'No refund' },
    ],
  },
  quote_expiration_hours: 72,
  invoice_delivery: 'on_acceptance',
  payment_required_to_confirm: true,
  minimum_hours: 1,
  service_charge_percent: 0,
  gratuity_percent: 0,
  tax_rate: 0,
  terms_text:
    'Quote is subject to vehicle availability and acceptance of the stated cancellation and payment terms.',
};

const paymentTermLabels: Record<string, string> = {
  due_on_receipt: 'Due on receipt',
  net_15: 'Net 15',
  net_30: 'Net 30',
  net_60: 'Net 60',
  net_90: 'Net 90',
};

export function mergeQuoteTerms(
  value?: QuoteTermsShape | null,
  fallback?: QuoteTermsShape | null
): QuoteTermsShape {
  const base = fallback || DEFAULT_TERMS;
  return {
    ...base,
    ...(value || {}),
    deposit: { ...(base.deposit || {}), ...((value || {}).deposit || {}) },
    cancellation_policy: {
      ...(base.cancellation_policy || {}),
      ...((value || {}).cancellation_policy || {}),
      rules:
        value?.cancellation_policy?.rules ||
        base.cancellation_policy?.rules ||
        [],
    },
  };
}

function valuesMatch(first: unknown, second: unknown) {
  return JSON.stringify(first ?? null) === JSON.stringify(second ?? null);
}

function formatPercent(value?: number, scale = 1) {
  const numeric = Number(value || 0);
  return `${(Number.isFinite(numeric) ? numeric * scale : 0).toFixed(
    scale === 100 ? 2 : 0
  )}%`;
}

function toTaxPercentInput(value?: number) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? Number((numeric * 100).toFixed(4)) : 0;
}

export function getDefaultQuoteTerms() {
  return mergeQuoteTerms(DEFAULT_TERMS);
}

export function compactQuoteTermsOverride(
  value?: QuoteTermsShape | null,
  fallback?: QuoteTermsShape | null
): QuoteTermsShape | null {
  if (!value) {
    return null;
  }

  const effective = mergeQuoteTerms(value, fallback);
  const base = mergeQuoteTerms(fallback || DEFAULT_TERMS);
  const override: QuoteTermsShape = {};

  (
    [
      'payment_terms',
      'quote_expiration_hours',
      'invoice_delivery',
      'payment_required_to_confirm',
      'minimum_hours',
      'service_charge_percent',
      'gratuity_percent',
      'tax_rate',
      'terms_text',
    ] as Array<keyof QuoteTermsShape>
  ).forEach((key) => {
    if (!valuesMatch(effective[key], base[key])) {
      (override as any)[key] = effective[key];
    }
  });

  const deposit: QuoteTermsShape['deposit'] = {};
  (['type', 'value', 'due', 'due_hours_before_service'] as const).forEach((key) => {
    if (!valuesMatch(effective.deposit?.[key], base.deposit?.[key])) {
      deposit[key] = effective.deposit?.[key] as any;
    }
  });
  if (Object.keys(deposit).length > 0) {
    override.deposit = deposit;
  }

  if (
    !valuesMatch(
      effective.cancellation_policy?.rules,
      base.cancellation_policy?.rules
    )
  ) {
    override.cancellation_policy = {
      rules: effective.cancellation_policy?.rules || [],
    };
  }

  return Object.keys(override).length > 0 ? override : null;
}

export function QuoteTermsSummary({
  terms,
  fallback,
  compact = false,
}: {
  terms?: QuoteTermsShape | null;
  fallback?: QuoteTermsShape | null;
  compact?: boolean;
}) {
  const effective = mergeQuoteTerms(terms, fallback);
  const deposit = effective.deposit || {};
  const rule = effective.cancellation_policy?.rules?.[0];
  const depositLabel =
    deposit.type === 'none'
      ? 'No deposit'
      : deposit.type === 'fixed'
      ? `$${Number(deposit.value || 0).toFixed(2)} deposit`
      : `${Number(deposit.value || 0).toFixed(0)}% deposit`;
  const depositDueLabel =
    deposit.due === 'before_trip'
      ? `${Math.max(1, Number(deposit.due_hours_before_service || 24))} hours before service`
      : deposit.due === 'with_invoice'
      ? 'With first invoice'
      : 'On acceptance';

  return (
    <div
      className={
        compact
          ? 'grid gap-2 text-sm md:grid-cols-2'
          : 'grid gap-3 text-sm md:grid-cols-3'
      }
    >
      <div className="rounded-card border border-outline/60 bg-surface-container-low p-3">
        <p className="text-xs font-medium uppercase text-on-surface-variant">
          Payment
        </p>
        <p className="mt-1 font-semibold text-on-surface">
          {paymentTermLabels[effective.payment_terms || ''] ||
            effective.payment_terms}
        </p>
      </div>
      <div className="rounded-card border border-outline/60 bg-surface-container-low p-3">
        <p className="text-xs font-medium uppercase text-on-surface-variant">
          Deposit
        </p>
        <p className="mt-1 font-semibold text-on-surface">{depositLabel}</p>
        {deposit.type !== 'none' ? (
          <p className="mt-1 text-xs text-on-surface-variant">{depositDueLabel}</p>
        ) : null}
      </div>
      <div className="rounded-card border border-outline/60 bg-surface-container-low p-3">
        <p className="text-xs font-medium uppercase text-on-surface-variant">
          Expiration
        </p>
        <p className="mt-1 font-semibold text-on-surface">
          {effective.quote_expiration_hours || 0} hours
        </p>
      </div>
      <div className="rounded-card border border-outline/60 bg-surface-container-low p-3">
        <p className="text-xs font-medium uppercase text-on-surface-variant">
          Minimum
        </p>
        <p className="mt-1 font-semibold text-on-surface">
          {effective.minimum_hours || 0} hours
        </p>
      </div>
      <div className="rounded-card border border-outline/60 bg-surface-container-low p-3">
        <p className="text-xs font-medium uppercase text-on-surface-variant">
          Charges
        </p>
        <p className="mt-1 font-semibold text-on-surface">
          Service {formatPercent(effective.service_charge_percent)} · Gratuity{' '}
          {formatPercent(effective.gratuity_percent)} · Tax{' '}
          {formatPercent(effective.tax_rate, 100)}
        </p>
      </div>
      <div className="rounded-card border border-outline/60 bg-surface-container-low p-3">
        <p className="text-xs font-medium uppercase text-on-surface-variant">
          Cancellation
        </p>
        <p className="mt-1 font-semibold text-on-surface">
          {rule
            ? `${rule.refund_percent}% refund ${rule.hours_before_pickup}+ hours out`
            : 'No rules'}
        </p>
      </div>
    </div>
  );
}

export default function QuoteTermsEditor({
  value,
  fallback,
  onChange,
  inheritanceMode = false,
}: {
  value?: QuoteTermsShape | null;
  fallback?: QuoteTermsShape | null;
  onChange: (next: QuoteTermsShape) => void;
  inheritanceMode?: boolean;
}) {
  const effective = mergeQuoteTerms(value, fallback);
  const patch = (next: Partial<QuoteTermsShape>) =>
    onChange(mergeQuoteTerms(next, effective));
  const setDeposit = (next: Partial<NonNullable<QuoteTermsShape['deposit']>>) =>
    patch({ deposit: { ...(effective.deposit || {}), ...next } });
  const rules = effective.cancellation_policy?.rules || [];

  const setRule = (index: number, next: Partial<(typeof rules)[number]>) => {
    const nextRules = rules.map((rule, i) =>
      i === index ? { ...rule, ...next } : rule
    );
    patch({
      cancellation_policy: {
        ...(effective.cancellation_policy || {}),
        rules: nextRules,
      },
    });
  };

  const removeRule = (index: number) => {
    patch({
      cancellation_policy: {
        ...(effective.cancellation_policy || {}),
        rules: rules.filter((_rule, i) => i !== index),
      },
    });
  };

  return (
    <div className="space-y-5">
      {inheritanceMode ? (
        <div className="rounded-card border border-outline/60 bg-surface-container-low p-3 text-sm text-on-surface-variant">
          Fields saved here become account or event overrides. Use reset to
          inherit tenant defaults again.
        </div>
      ) : null}

      <section className="rounded-card border border-outline/60 p-4">
        <h4 className="text-sm font-semibold text-on-surface">
          Payment and deposit
        </h4>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <label className="block">
            <span className="text-xs font-medium text-on-surface-variant">
              Payment terms
            </span>
            <select
              className="input-field mt-1"
              value={effective.payment_terms || 'due_on_receipt'}
              onChange={(e) => patch({ payment_terms: e.target.value })}
            >
              <option value="due_on_receipt">Due on receipt</option>
              <option value="net_15">Net 15</option>
              <option value="net_30">Net 30</option>
              <option value="net_60">Net 60</option>
              <option value="net_90">Net 90</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-on-surface-variant">
              Deposit
            </span>
            <select
              className="input-field mt-1"
              value={effective.deposit?.type || 'percent'}
              onChange={(e) => setDeposit({ type: e.target.value as any })}
            >
              <option value="none">No deposit</option>
              <option value="percent">Percentage</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-on-surface-variant">
              Deposit value
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input-field mt-1"
              value={effective.deposit?.value ?? 0}
              onChange={(e) => setDeposit({ value: Number(e.target.value) })}
              disabled={effective.deposit?.type === 'none'}
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-on-surface-variant">
              Deposit due
            </span>
            <select
              className="input-field mt-1"
              value={effective.deposit?.due || 'on_acceptance'}
              onChange={(e) => setDeposit({ due: e.target.value as any })}
            >
              <option value="on_acceptance">On acceptance</option>
              <option value="before_trip">Before trip</option>
              <option value="with_invoice">With invoice</option>
            </select>
          </label>
          {effective.deposit?.type !== 'none' &&
          effective.deposit?.due === 'before_trip' ? (
            <label className="block">
              <span className="text-xs font-medium text-on-surface-variant">
                Hours before service
              </span>
              <input
                type="number"
                min="1"
                step="1"
                className="input-field mt-1"
                value={effective.deposit?.due_hours_before_service ?? 24}
                onChange={(e) =>
                  setDeposit({
                    due_hours_before_service: Math.max(
                      1,
                      Math.round(Number(e.target.value || 24))
                    ),
                  })
                }
              />
            </label>
          ) : null}
          <label className="block">
            <span className="text-xs font-medium text-on-surface-variant">
              Invoice delivery
            </span>
            <select
              className="input-field mt-1"
              value={effective.invoice_delivery || 'on_acceptance'}
              onChange={(e) =>
                patch({ invoice_delivery: e.target.value as any })
              }
            >
              <option value="on_acceptance">On acceptance</option>
              <option value="after_trip">After trip</option>
              <option value="manual">Manual</option>
            </select>
          </label>
          <label className="flex items-center gap-2 pt-6 text-sm text-on-surface">
            <input
              type="checkbox"
              checked={Boolean(effective.payment_required_to_confirm)}
              onChange={(e) =>
                patch({ payment_required_to_confirm: e.target.checked })
              }
            />
            Payment required to confirm
          </label>
        </div>
      </section>

      <section className="rounded-card border border-outline/60 p-4">
        <h4 className="text-sm font-semibold text-on-surface">
          Timing and charges
        </h4>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {[
            ['Quote expires after', 'quote_expiration_hours', 1],
            ['Minimum hours', 'minimum_hours', 1],
            ['Service charge %', 'service_charge_percent', 0.01],
            ['Gratuity %', 'gratuity_percent', 0.01],
          ].map(([label, key, step]) => (
            <label className="block" key={key as string}>
              <span className="text-xs font-medium text-on-surface-variant">
                {label}
              </span>
              <input
                type="number"
                min="0"
                step={step as number}
                className="input-field mt-1"
                value={(effective as any)[key as string] ?? 0}
                onChange={(e) =>
                  patch({
                    [key as string]: Number(e.target.value),
                  } as QuoteTermsShape)
                }
              />
            </label>
          ))}
          <label className="block">
            <span className="text-xs font-medium text-on-surface-variant">
              Tax %
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input-field mt-1"
              value={toTaxPercentInput(effective.tax_rate)}
              onChange={(e) =>
                patch({ tax_rate: Number(e.target.value) / 100 })
              }
            />
          </label>
        </div>
      </section>

      <section className="rounded-card border border-outline/60 p-4">
        <div className="flex items-center justify-between gap-3">
          <h4 className="text-sm font-semibold text-on-surface">
            Cancellation rules
          </h4>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() =>
              patch({
                cancellation_policy: {
                  ...(effective.cancellation_policy || {}),
                  rules: [
                    ...rules,
                    { hours_before_pickup: 0, refund_percent: 0 },
                  ],
                },
              })
            }
          >
            Add Rule
          </button>
        </div>
        <div className="mt-3 space-y-3">
          {rules.map((rule, index) => (
            <div
              key={`${index}-${rule.hours_before_pickup}`}
              className="grid gap-3 rounded-card bg-surface-container-low p-3 md:grid-cols-[1fr_1fr_2fr_auto]"
            >
              <label>
                <span className="text-xs font-medium text-on-surface-variant">
                  Hours before pickup
                </span>
                <input
                  type="number"
                  min="0"
                  className="input-field mt-1"
                  value={rule.hours_before_pickup}
                  onChange={(e) =>
                    setRule(index, {
                      hours_before_pickup: Number(e.target.value),
                    })
                  }
                />
              </label>
              <label>
                <span className="text-xs font-medium text-on-surface-variant">
                  Refund %
                </span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="input-field mt-1"
                  value={rule.refund_percent}
                  onChange={(e) =>
                    setRule(index, { refund_percent: Number(e.target.value) })
                  }
                />
              </label>
              <label>
                <span className="text-xs font-medium text-on-surface-variant">
                  Description
                </span>
                <input
                  type="text"
                  className="input-field mt-1"
                  value={rule.description || ''}
                  onChange={(e) =>
                    setRule(index, { description: e.target.value })
                  }
                />
              </label>
              <button
                type="button"
                className="btn-secondary self-end text-xs"
                onClick={() => removeRule(index)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-card border border-outline/60 p-4">
        <h4 className="text-sm font-semibold text-on-surface">Terms text</h4>
        <textarea
          className="input-field mt-3 min-h-[120px]"
          value={effective.terms_text || ''}
          onChange={(e) => patch({ terms_text: e.target.value })}
        />
      </section>
    </div>
  );
}
